import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENV = import.meta.env.VITE_SENTRY_ENV as string | undefined
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined

const PII_PATTERNS = [
  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i,
  /01[016789][- ]?\d{3,4}[- ]?\d{4}/,
]

const PII_REDACTED = '[REDACTED]'

// 단위 테스트용으로 export — 보안 핵심 로직이라 직접 검증 가치 큼.
export function containsPII(value: unknown): boolean {
  if (typeof value !== 'string') return false
  return PII_PATTERNS.some((pattern) => pattern.test(value))
}

export function scrubRecord(
  record: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!record) return record
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(record)) {
    cleaned[key] = containsPII(value) ? PII_REDACTED : value
  }
  return cleaned
}

// iOS WKWebView teardown race — 네이티브가 script message handler 를 제거한 뒤
// flutter_inappwebview 내부 JS 의 postMessage 가 호출되면 InvalidAccessError 가
// unhandled rejection 으로 남는다 (앱 동작 영향 없음, gacha ONL-WEB-GACHA-2 와 동일 클래스).
// 앱 코드 프레임이 전혀 없는 네이티브 postMessage 단일 프레임만 정밀 매칭해 drop —
// useBridge 를 거친 실제 브릿지 실패는 `[Bridge] ...` Error 로 래핑되어 살아남는다.
function isNativePostMessageNoise(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0]
  if (exception?.type !== 'InvalidAccessError') return false

  const frames = exception.stacktrace?.frames
  if (frames?.length !== 1) return false

  return frames[0].function === 'postMessage' && frames[0].filename === '[native code]'
}

export function initSentry(): void {
  if (!DSN) return

  Sentry.init({
    dsn: DSN,
    environment: ENV ?? 'dev',
    release: RELEASE,
    sampleRate: 1.0,
    tracesSampleRate: 0,
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
      /Loading chunk \d+ failed/,
    ],
    beforeSend(event) {
      if (isNativePostMessageNoise(event)) return null
      if (event.extra) {
        event.extra = scrubRecord(event.extra)
      }
      if (event.contexts) {
        for (const [key, ctx] of Object.entries(event.contexts)) {
          if (ctx && typeof ctx === 'object') {
            event.contexts[key] = scrubRecord(ctx as Record<string, unknown>)
          }
        }
      }
      return event
    },
  })
}

export function setSentryUser(userId: string | null): void {
  if (!DSN) return
  Sentry.setUser(userId ? { id: userId } : null)
}

/** 결제 관측용 컨텍스트 — 식별자만(orderId/paymentId/method/step). 이름·전화 등 PII 금지. */
export interface PaymentObservabilityContext {
  /** 결제수단 (card | kakaopay | naverpay) */
  method?: string
  /** 결제 단계 (payment | process | result | card-register 등) */
  step?: string
  orderId?: string | number
  paymentId?: string | number
}

/**
 * 결제 플로우 breadcrumb — Sentry 이벤트 직전 마일스톤(주문생성→PG진입→콜백→완료) 기록.
 * data 는 scrubRecord 로 PII 마스킹. DSN 없으면 no-op.
 */
export function addPaymentBreadcrumb(message: string, data?: Record<string, unknown>): void {
  if (!DSN) return
  Sentry.addBreadcrumb({
    category: 'payment',
    level: 'info',
    message,
    data: scrubRecord(data),
  })
}

/**
 * 결제 컨텍스트 태그/컨텍스트 설정 — method·step 은 태그(필터·그룹핑), 식별자는 context.
 * DSN 없으면 no-op. 전달된 필드만 갱신.
 */
export function setPaymentContext(ctx: PaymentObservabilityContext): void {
  if (!DSN) return
  if (ctx.method != null) Sentry.setTag('payment.method', ctx.method)
  if (ctx.step != null) Sentry.setTag('payment.step', ctx.step)
  Sentry.setContext('payment', scrubRecord(ctx as Record<string, unknown>) ?? null)
}

export { Sentry }
